import { useMemo, useState } from 'react';
import { Segment, Message, Button } from 'semantic-ui-react';
import { TitleSegment, SuiTable, SuiTableState } from '@britishcouncil/react-admin';
import { Column } from 'react-table';
import styled from '@emotion/styled';

import type { AuditType, AuditHistoryRecord } from 'api';
import { useGetAuditHistoryQuery } from 'store/audit';
import { formatDate } from 'utils';
import type { AuditColumn } from './auditHistoryTypes';
import { AuditHistoryActionLabel } from './AuditHistoryActionLabel';
import { CheckIcon } from 'components/CheckIcon';

type AuditHistoryProps = {
  columns: AuditColumn[];
  onHistoryHide?: () => void;
} & (
  | { auditType: AuditType; domainId: number }
  | { auditType?: never; historyRecords: AuditHistoryRecord[]; isLoading: boolean }
);

const INITIAL_TABLE_STATE: SuiTableState<AuditHistoryRecord> = {
  pageIndex: 0,
  pageSize: 10,
  sortBy: [],
};

export const AuditHistory = (props: AuditHistoryProps) => {
  const { auditType, columns, onHistoryHide } = props;
  const [tableState, setTableState] = useState(INITIAL_TABLE_STATE);

  const { data, isLoading, error } = useGetAuditHistoryQuery(
    {
      auditType: auditType!,
      domainId: auditType ? props.domainId : 0,
      pageIndex: tableState.pageIndex,
      pageSize: tableState.pageSize,
    },
    { skip: !auditType }
  );

  const formatValue = (columnDef: AuditColumn, record: AuditHistoryRecord) => {
    const column = record.data[columnDef.field];

    if (!column || column.value === null || column.value === undefined) {
      return 'NA';
    }

    const { value } = column;

    if (columnDef.render) {
      return columnDef.render(record);
    }

    switch (columnDef.type) {
      case 'date':
        return formatDate(value);
      case 'boolean':
        return <CheckIcon value={value} />;
      default:
        return String(value);
    }
  };

  const tableColumns = useMemo<Column<AuditHistoryRecord>[]>(() => {
    const baseColumns: Column<AuditHistoryRecord>[] = [
      {
        id: 'createdOn',
        Header: 'Date of action',
        accessor: 'createdOn',
        Cell: ({ value }) => <>{formatDate(value)}</>,
      },
      {
        id: 'createdBy',
        Header: 'Actioned by',
        accessor: 'createdBy',
      },
      {
        id: 'action',
        Header: 'Action type',
        accessor: 'action',
        Cell: ({ value }) => <AuditHistoryActionLabel action={value} />,
      },
    ];

    const dataColumns: Column<AuditHistoryRecord>[] = columns.map((col) => ({
      id: col.field,
      Header: col.title,
      Cell: ({ row }: { row: { original: AuditHistoryRecord } }) => {
        const action = row.original.action.toLowerCase();
        const column = row.original.data[col.field];
        const wasChanged = column.wasChanged || ['insert', 'delete', 'restore'].includes(action);

        return <S.Cell bolded={wasChanged}>{formatValue(col, row.original)}</S.Cell>;
      },
    }));

    return [...baseColumns, ...dataColumns];
  }, [columns]);

  if (error) {
    return <Message negative>Failed to load audit history data.</Message>;
  }

  const _data = auditType ? data?.results : props.historyRecords;
  const _isLoading = auditType ? isLoading : props.isLoading;

  return (
    <S.Container>
      <Segment.Group>
        <TitleSegment
          title="History of changes"
          icon="history"
          rightSection={
            onHistoryHide && (
              <Button primary size="tiny" onClick={onHistoryHide}>
                Hide history
              </Button>
            )
          }
        />

        {_data?.length === 0 && !_isLoading && (
          <Segment>
            <Message info>
              <Message.Header>No History Available</Message.Header>
              <p>There are no audit records for this item.</p>
            </Message>
          </Segment>
        )}

        <SuiTable
          disableFilters
          disableSortBy
          data={_data ?? []}
          state={!auditType ? undefined : tableState}
          onStateChange={!auditType ? undefined : setTableState}
          loading={_isLoading}
          totalCount={!auditType ? undefined : (data?.count ?? 0)}
          columns={tableColumns}
          tableRowProps={(_, { row }) => ({
            negative: row.original.action === 'Delete',
            positive: row.original.action === 'Insert' || row.original.action === 'Restore',
            className: row.original.action === 'Delete' ? 'deleted-record' : '',
          })}
        />
      </Segment.Group>
    </S.Container>
  );
};

const S = {
  Container: styled.div`
    margin-top: 2rem;
  `,
  Cell: styled.div<{ bolded: boolean }>`
    font-weight: ${(props) => (props.bolded ? 'bold' : 'normal')};
  `,
};
